import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore, useSettingsStore } from './stores/appStore';
import { ToastContainer } from './components/Toast';
import { LoadingScreen } from './components/LoadingScreen';

// API base URL
const API_BASE = import.meta.env.VITE_API_URL || '';

// Lazy-loaded pages
const AuthPage = lazy(() => import('./pages/AuthPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const RoomPage = lazy(() => import('./pages/RoomPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
}

// Public route wrapper (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

export default function App() {
  const { language, theme } = useSettingsStore();
  const { refreshToken, setLoading, setUser, setTokens, logout } = useAuthStore();
  
  // Initialize app settings
  useEffect(() => {
    // Set initial direction and theme
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.setAttribute('data-theme', theme);
  }, [language, theme]);
  
  // Check authentication on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem('razmucode-auth');
    const hasToken = storedAuth && JSON.parse(storedAuth).state?.refreshToken;

    async function checkAuth() {
      if (!refreshToken && !hasToken) {
        setLoading(false);
        return;
      }
      
      const tokenToUse = refreshToken || (hasToken ? JSON.parse(storedAuth!).state.refreshToken : null);
      
      if (!tokenToUse) {
         setLoading(false);
         return;
      }

      try {
        // Try to refresh the token
        const response = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: tokenToUse }),
        });
        
        if (!response.ok) {
          throw new Error('Refresh failed');
        }
        
        const data = await response.json();
        
        if (data.success) {
          setTokens(data.data.accessToken, data.data.refreshToken);
          
          // Get user info
          const userResponse = await fetch(`${API_BASE}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${data.data.accessToken}`,
            },
          });
          
          if (userResponse.ok) {
            const userData = await userResponse.json();
            if (userData.success) {
              setUser(userData.data.user);
              return;
            }
          }
        }
        
        throw new Error('Auth check failed');
      } catch {
        logout();
      } finally {
        setLoading(false);
      }
    }
    
    checkAuth();
  }, []);
  
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route
            path="/auth"
            element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/room/:roomId"
            element={
              <ProtectedRoute>
                <RoomPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <ToastContainer />
    </BrowserRouter>
  );
}
